/**
 * One-shot: add the Earnings this week pane to saved "Market Outlook"
 * workspaces that were seeded before it existed. Idempotent. Flagged by
 * `mpp:migrate:outlook-earnings-week:v1`.
 *
 * Lands as a full-width row under the existing tree so the week calendar
 * has the width Mon–Fri columns need. Trees that already contain
 * `earningsWeek` are left alone — a user who removed it stays removed
 * after the flag is set, which is why this is a one-shot, not a sync.
 */
import { safeLocalStorageGet, safeLocalStorageSet } from "@/lib/safeLocalStorage";
import type { WsNode, WorkspacePage } from "@/lib/workspace/workspacePages";

const FLAG = "mpp:migrate:outlook-earnings-week:v1";
const LIST_KEY = "mpp:workspaces:v1";
const PAGE_NAME = "market outlook";
const COMPONENT = "earningsWeek";

function nid(prefix: string): string {
  return `${prefix}${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

function treeHas(node: WsNode, id: string): boolean {
  if (node.t === "leaf") return node.component === id;
  return node.children.some((c) => treeHas(c, id));
}

function injectBelow(root: WsNode): WsNode {
  const leaf: WsNode = { id: nid("l"), t: "leaf", component: COMPONENT };
  return { id: nid("s"), t: "split", dir: "v", children: [root, leaf] };
}

function rewriteTreeJson(raw: string): string | null {
  try {
    const root = JSON.parse(raw) as WsNode;
    if (!root || (root.t !== "leaf" && root.t !== "split")) return null;
    if (treeHas(root, COMPONENT)) return null;
    return JSON.stringify(injectBelow(root));
  } catch {
    return null;
  }
}

export function migrateOutlookEarningsWeek(): void {
  if (typeof window === "undefined") return;
  if (safeLocalStorageGet(FLAG) === "1") return;

  let pool: WorkspacePage[] = [];
  try {
    const raw = safeLocalStorageGet(LIST_KEY);
    if (raw) {
      const arr = JSON.parse(raw) as WorkspacePage[];
      if (Array.isArray(arr)) pool = arr;
    }
  } catch {
    pool = [];
  }

  const outlookIds = new Set<string>();
  let poolChanged = false;
  const nextPool: WorkspacePage[] = pool.map((p) => {
    if (!p?.root) return p;
    const isOutlook = typeof p.name === "string" && p.name.trim().toLowerCase() === PAGE_NAME;
    if (!isOutlook) return p;
    outlookIds.add(p.id);
    if (treeHas(p.root, COMPONENT)) return p;
    poolChanged = true;
    return { ...p, root: injectBelow(p.root), updatedAt: Date.now() };
  });
  if (poolChanged) safeLocalStorageSet(LIST_KEY, JSON.stringify(nextPool));

  try {
    const writes: Array<{ key: string; value: string }> = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (!key || !key.includes("mpp:ws-tree:")) continue;
      const matchesOutlook = [...outlookIds].some((id) => key.includes(id));
      if (!matchesOutlook) continue;
      const raw = window.localStorage.getItem(key);
      if (!raw) continue;
      const next = rewriteTreeJson(raw);
      if (next) writes.push({ key, value: next });
    }
    for (const { key, value } of writes) {
      safeLocalStorageSet(key.includes("\u0000@dp:") ? key.split("\u0000@dp:")[0]! : key, value);
      if (key.includes("\u0000@dp:")) {
        try {
          window.localStorage.setItem(key, value);
        } catch {
          /* quota — logical write above is the one that matters */
        }
      }
    }
  } catch {
    /* pool migration still stands */
  }

  safeLocalStorageSet(FLAG, "1");
}
